import React from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { fToNow } from "../../../utils/formatTime";
import {
    getItemGroups,
    getItemKey,
    getItemNotes,
    isCompletedStatus,
} from "../utils/orderItems";
import { parseDateSafe } from "../utils/orderTime";

export default function OrderItemsList({
    items,
    t,
    order,
    showItemStatus = false,
    onAdvanceItem,
    isItemDisabled,
    getNextStatus,
    distanceLocale,
}) {
    const safeItems = Array.isArray(items) ? items : [];
    if (safeItems.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t("kds.noItems")}
            </Typography>
        );
    }
    const groups = getItemGroups(safeItems, order?.entry_items);
    if (showItemStatus) {
        groups.sort(
            (a, b) =>
                Number(
                    a.entries.every(({ item }) =>
                        isCompletedStatus(item.status)
                    )
                ) -
                Number(
                    b.entries.every(({ item }) =>
                        isCompletedStatus(item.status)
                    )
                )
        );
    }

    const renderItemRow = (it, depth = 0, contextOnly = false) => {
        const notes = getItemNotes(it);
        const nextStatus = getNextStatus?.(it, safeItems, order);
        const canAdvance =
            !contextOnly && !isCompletedStatus(it?.status) && !!order && !!it?.identifier && !!nextStatus;
        const actionSlotWidth = 104;
        const completedAt =
            isCompletedStatus(it?.status) && it?.status_change_date
                ? parseDateSafe(it.status_change_date)
                : null;

        return (
            <Box
                key={getItemKey(it, depth ? "-addon" : "")}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    paddingInlineStart: depth * 2,
                    py: 0.35,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        gap: 0.75,
                    }}
                >
                    <Box
                        sx={{
                            minWidth: 36,
                            display: "flex",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Chip
                            size="small"
                            label={`x${it?.qty ?? 0}`}
                            sx={{ fontWeight: 700, mt: 0.1 }}
                            color="default"
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: depth ? 500 : 800,
                                lineHeight: 1.25,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                            }}
                        >
                            {it?.item_name ||
                                it?.item_code ||
                                t("kds.itemFallback")}
                        </Typography>
                        {notes ? (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    whiteSpace: "normal",
                                    overflowWrap: "anywhere",
                                    display: "block",
                                    mt: 0.25,
                                }}
                            >
                                {notes}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>

                {showItemStatus && !contextOnly && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.8,
                            pl: 4.6,
                            minHeight: 30,
                        }}
                    >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {it?.status ? (
                                <Stack
                                    direction="row"
                                    spacing={0.6}
                                    alignItems="center"
                                    flexWrap="nowrap"
                                    sx={{ minWidth: 0 }}
                                >
                                    <Chip
                                        size="small"
                                        label={isCompletedStatus(it.status) ? t("kds.completed") : it.status}
                                        color={
                                            canAdvance ? "warning" : "success"
                                        }
                                        sx={{
                                            height: 22,
                                            maxWidth: "100%",
                                            "& .MuiChip-label": {
                                                whiteSpace: "nowrap",
                                            },
                                        }}
                                    />
                                    {completedAt ? (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                whiteSpace: "nowrap",
                                                lineHeight: 1,
                                            }}
                                        >
                                            {fToNow(
                                                completedAt,
                                                distanceLocale
                                            )}
                                        </Typography>
                                    ) : null}
                                </Stack>
                            ) : null}
                        </Box>

                        <Box
                            sx={{
                                width: actionSlotWidth,
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            {canAdvance ? (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    onClick={() =>
                                        onAdvanceItem?.(order, it, safeItems)
                                    }
                                    disabled={isItemDisabled?.(
                                        order?.name,
                                        it?.identifier
                                    )}
                                    sx={{
                                        width: "100%",
                                        minHeight: 30,
                                        px: 0.75,
                                        whiteSpace: "nowrap",
                                        fontWeight: 700,
                                        lineHeight: 1,
                                    }}
                                >
                                    {t("kds.markReady")}
                                </Button>
                            ) : (
                                <Box sx={{ width: "100%", minHeight: 30 }} />
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Stack spacing={1.25}>
            {groups.map(({ root, entries }) => (
                <Box
                    key={getItemKey(root)}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        px: 1.25,
                        py: 0.75,
                    }}
                >
                    {entries.map(({ item, depth, contextOnly }) =>
                        renderItemRow(item, depth, contextOnly)
                    )}
                </Box>
            ))}
        </Stack>
    );
}
